/**
 * The maps the app knows, with the values of Valve's overview files
 * (resource/overviews/<map>.txt): where the top-left corner of the 1024 px
 * radar sits in the world, and how many world units one radar pixel covers.
 */
export interface MapInfo {
  /** Game name, as CS2 reports it: "de_mirage". */
  id: MapId;
  /** Display name: "Mirage". */
  name: string;
  /** World X of the radar's left edge. */
  posX: number;
  /** World Y of the radar's top edge. */
  posY: number;
  /** World units per radar pixel. */
  scale: number;
  /** Maps with two levels: below this height (world Z), the lower radar applies. */
  lowerBelowZ?: number;
  /** In the Active Duty pool (30 August 2026). */
  activeDuty: boolean;
}

export const RADAR_SIZE = 1024;

export const MAP_IDS = ["de_ancient", "de_anubis", "de_cache", "de_dust2", "de_inferno", "de_mirage", "de_nuke"] as const;
export type MapId = (typeof MAP_IDS)[number];

export const MAPS: readonly MapInfo[] = [
  { id: "de_ancient", name: "Ancient", posX: -2953, posY: 2164, scale: 5, activeDuty: true },
  { id: "de_anubis", name: "Anubis", posX: -2796, posY: 3328, scale: 5.22, activeDuty: true },
  { id: "de_cache", name: "Cache", posX: -2000, posY: 3250, scale: 5.5, activeDuty: true },
  { id: "de_dust2", name: "Dust II", posX: -2476, posY: 3239, scale: 4.4, activeDuty: true },
  { id: "de_inferno", name: "Inferno", posX: -2087, posY: 3870, scale: 4.9, activeDuty: true },
  { id: "de_mirage", name: "Mirage", posX: -3230, posY: 1713, scale: 5, activeDuty: true },
  { id: "de_nuke", name: "Nuke", posX: -3453, posY: 2887, scale: 7, lowerBelowZ: -495, activeDuty: true },
];

export const isMapId = (value: unknown): value is MapId => typeof value === "string" && (MAP_IDS as readonly string[]).includes(value);

export function mapInfo(id: MapId): MapInfo {
  const map = MAPS.find((m) => m.id === id);
  if (!map) throw new Error(`Unknown map ${id}`);
  return map;
}

export type Level = "upper" | "lower";

/** File name of a map's radar image in radars/. */
export const radarFile = (id: MapId, level: Level = "upper") => (level === "lower" ? `${id}_lower.webp` : `${id}.webp`);
